import React from "react";
import PageFrame from "./PageFrame";
import PageHeading from "./PageHeading";
import Abs from "./Abs";
import BellCurve from "./BellCurve";

/**
 * Page 04 - Value Analysis.
 *
 * A fixed composition: the curve sits at x 142..914 / y 90..265, the facts band between
 * the rules at y=380 and y=442, and the two copy columns start at y=451 either side of
 * the divider at x=512. The pricing band and the four facts are real markup rather than
 * text positioned over a background image, so long values reflow instead of colliding.
 */

/**
 * Placeholder in the Qualifications copy for the total closed transaction count. Substituted
 * here rather than in bov-boilerplate so the copy constants stay free of live figures.
 *
 * Plain text: the count was a link to the closed-transactions list for a while, and the
 * business asked for the number on its own. Nothing to render but a string, so a line with no
 * token passes through untouched.
 */
const TRANSACTIONS_TOKEN = '{transactions}';

function renderLine(line, transactions) {
    return String(line).split(TRANSACTIONS_TOKEN).join(transactions || '-');
}

export default function ValueAnalysis(props) {
    const {pricing, facts, copy, transactions, productType, subProduct, pageNo, totalPages} = props;

    // Retail prints price and cap rate under each band; office and industrial add PSF between
    // them. Keyed off the product rather than listed per deck, so a new office or industrial
    // combination picks it up without another entry.
    //
    // Multi-Family follows retail: its reference prints price and cap rate only, an apartment
    // deal being priced per unit rather than per foot.
    const showPsf = ['retail', 'multi-family'].indexOf(String(productType || '').trim().toLowerCase()) === -1;

    return (
        <PageFrame fixed pageNo={pageNo} totalPages={totalPages}>
            <PageHeading
                eyebrow={`PRICING MATRIX  •  ${subProduct}`}
                title='Value '
                titleAccent='Analysis.'
            />

            <Abs x={142} y={90} w={772}>
                <BellCurve
                    conservative={pricing.conservative}
                    anticipated={pricing.anticipated}
                    aggressive={pricing.aggressive}
                    showPsf={showPsf}
                />
            </Abs>

            <Abs x={48} y={380} w={960}>
                <div className='bov-facts'>
                    {facts.map((fact, i) => (
                        <div key={i} className='bov-facts__item'>
                            <span className='bov-facts__value'>{fact.value}</span>
                            <span className='bov-facts__label'>{fact.label}</span>
                        </div>
                    ))}
                </div>
            </Abs>

            <Abs x={48} y={451} w={960}>
                <div className='bov-copy'>
                    <div className='bov-copy__col'>
                        <CopyBlock title='STRENGTHS' lines={copy.strengths}/>
                        <CopyBlock title='CHALLENGES' lines={copy.challenges}/>
                        <CopyBlock title='BUYER POOL' lines={copy.buyerPool}/>
                    </div>
                    <div className='bov-copy__col'>
                        <CopyBlock
                            title='QUALIFICATIONS'
                            lines={copy.qualifications}
                            transactions={transactions}
                        />
                        <CopyBlock title='RECOMMENDATIONS & TIMING' lines={copy.recommendations}/>
                    </div>
                </div>
            </Abs>
        </PageFrame>
    );
}

function CopyBlock({title, lines, transactions}) {
    if (!lines || !lines.length) return null;
    return (
        <div className='bov-copy__block'>
            <div className='bov-copy__title'>{title}</div>
            <ul className='bov-copy__list'>
                {lines.map((line, i) => <li key={i}>{renderLine(line, transactions)}</li>)}
            </ul>
        </div>
    );
}
